import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import GlassCard from '@/components/ui/glass-card';
import {
  cardHover,
  fadeIn,
  glassCard,
  iconHover,
  pageTransition,
  slideUp,
  staggerContainer,
  textReveal,
} from '@/lib/animations';
import { motion } from 'framer-motion';
import {
  BarChart3,
  Clock,
  MessageSquare,
  Phone,
  Plus,
  Stethoscope,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const Dashboard = () => {
  const aiModules = [
    {
      name: 'Ai Metric',
      description: 'Advanced analytics and insights powered by Ai',
      icon: BarChart3,
      path: '/ai-metric',
      gradient: 'from-blue-500 to-blue-600',
    },
    {
      name: 'Ai Scribe',
      description: 'Intelligent documentation and note-taking',
      icon: MessageSquare,
      path: '/ai-scribe',
      gradient: 'from-purple-500 to-blue-500',
    },
    {
      name: 'Ai Connect',
      description: 'Seamless communication and collaboration',
      icon: Phone,
      path: '/ai-connect',
      gradient: 'from-blue-500 to-purple-500',
    },
    {
      name: 'Ai Consult',
      description: 'Expert Ai-powered consultation',
      icon: Stethoscope,
      path: '/ai-consult',
      gradient: 'from-purple-500 to-blue-500',
    },
  ];

  const quickActions = [
    { name: 'New Analysis', icon: Plus, action: () => {} },
    { name: 'Quick Chat', icon: MessageSquare, action: () => {} },
  ];

  const recentActivity = [
    {
      type: 'Ai Analysis Completed',
      description: 'Your Ai-powered analysis is ready for review',
      time: '2h ago',
    },
    {
      type: 'Ai Analysis Completed',
      description: 'Your Ai-powered analysis is ready for review',
      time: '2h ago',
    },
    {
      type: 'Ai Analysis Completed',
      description: 'Your Ai-powered analysis is ready for review',
      time: '2h ago',
    },
  ];

  return (
    <Layout>
      <motion.div className="w-full space-y-8" {...pageTransition}>
        {/* Header */}
        <motion.div
          className="w-full space-y-2"
          variants={fadeIn}
          initial="initial"
          animate="animate">
          <motion.h1
            className="text-3xl md:text-4xl font-bold text-foreground mb-1"
            variants={textReveal}>
            Welcome to EyeVantage
          </motion.h1>
          <motion.p
            className="text-lg md:text-xl text-muted-foreground"
            variants={textReveal}>
            Your AI-powered productivity suite
          </motion.p>
        </motion.div>

        {/* AI Modules Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          variants={staggerContainer}
          initial="initial"
          animate="animate">
          {aiModules.map((module) => (
            <motion.div key={module.name} variants={glassCard}>
              <GlassCard>
                <Link to={module.path} className="block p-6">
                  <motion.div
                    className={`w-12 h-12 rounded-xl bg-gradient-to-r ${module.gradient} flex items-center justify-center mb-4 shadow-md`}
                    whileHover={iconHover}>
                    <module.icon className="w-6 h-6 text-white" />
                  </motion.div>
                  <motion.h3
                    className="text-lg font-semibold text-foreground mb-2"
                    variants={textReveal}>
                    {module.name}
                  </motion.h3>
                  <motion.p
                    className="text-muted-foreground text-sm"
                    variants={textReveal}>
                    {module.description}
                  </motion.p>
                </Link>
              </GlassCard>
            </motion.div>
          ))}
        </motion.div>

        {/* Quick Actions and Recent Activity */}
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          variants={staggerContainer}
          initial="initial"
          animate="animate">
          {/* Quick Actions */}
          <motion.div variants={glassCard}>
            <GlassCard>
              <div className="p-6">
                <motion.h2
                  className="text-xl font-semibold text-foreground mb-4"
                  variants={textReveal}>
                  Quick Actions
                </motion.h2>
                <div className="space-y-3">
                  {quickActions.map((action) => (
                    <motion.div
                      key={action.name}
                      variants={slideUp}
                      whileHover={cardHover}>
                      <GlassCard className="mb-2">
                        <Button
                          onClick={action.action}
                          className="relative w-full justify-start bg-transparent hover:bg-accent/30 text-foreground font-medium rounded-xl transition-all duration-200 p-4 flex items-center gap-4">
                          <action.icon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          <div className="flex flex-col items-start">
                            <span className="font-semibold text-base text-foreground">
                              {action.name}
                            </span>
                            <span className="text-sm text-muted-foreground">
                              {action.name === 'New Analysis'
                                ? 'Start a new AI-powered analysis'
                                : 'Chat instantly with AI'}
                            </span>
                          </div>
                        </Button>
                      </GlassCard>
                    </motion.div>
                  ))}
                </div>
              </div>
            </GlassCard>
          </motion.div>

          {/* Recent Activity */}
          <motion.div variants={glassCard}>
            <GlassCard>
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <motion.h2
                    className="text-xl font-semibold text-foreground"
                    variants={textReveal}>
                    Recent Activity
                  </motion.h2>
                  <motion.div whileHover={iconHover}>
                    <Clock className="w-5 h-5 text-muted-foreground" />
                  </motion.div>
                </div>
                <div className="space-y-4">
                  {recentActivity.map((activity, index) => (
                    <motion.div
                      key={index}
                      variants={slideUp}
                      className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground">
                          {activity.type}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {activity.description}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {activity.time}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </GlassCard>
          </motion.div>
        </motion.div>
      </motion.div>
    </Layout>
  );
};

export default Dashboard;
